import { Box, Button } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";

export interface ToggleSwitchOption<T extends string = string> {
  value: T;
  label: string;
}

export interface ToggleSwitchProps<T extends string = string> {
  options: [ToggleSwitchOption<T>, ToggleSwitchOption<T>];
  value: T;
  onChange: (value: T) => void;
  sx?: SxProps;
  buttonSx?: SxProps;
}

export function ToggleSwitch<T extends string = string>({
  options,
  value,
  onChange,
  sx,
  buttonSx,
}: ToggleSwitchProps<T>) {
  const handleContainerClick = () => {
    const otherOption = options.find((opt) => opt.value !== value);

    if (otherOption) onChange(otherOption.value);
  };

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        bgcolor: "background.level1",
        borderRadius: "xl",
        p: 0.375,
        gap: 0.375,
        cursor: "pointer",
        ...sx,
      }}
    >
      {options.map((option) => {
        const isActive = value === option.value;

        return (
          <Button
            key={option.value}
            variant={isActive ? "solid" : "plain"}
            color="neutral"
            onClick={(e) => {
              e.stopPropagation();
              handleContainerClick();
            }}
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, md: 0.875 },
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              fontWeight: 600,
              borderRadius: "lg",
              bgcolor: isActive ? "background.surface" : "transparent",
              color: isActive ? "text.primary" : "text.secondary",
              boxShadow: isActive ? "sm" : "none",
              transition: "all 0.2s",
              minHeight: "auto",
              "&:hover": {
                bgcolor: isActive ? "background.surface" : "transparent",
              },
              ...buttonSx,
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </Box>
  );
}
